import React from "react";
import {
  Box,
  HStack,
  Icon,
  Text,
  VStack,
  Progress,
  Badge,
} from "@chakra-ui/react";
import { FiCheckCircle, FiXCircle, FiCircle } from "react-icons/fi";

/**
 * Renders shiftDocumentation.formChecklist as a visual checklist.
 * Shows completion percentage and allows clicking incomplete items.
 * @param {{ checklist: Array, onItemClick?: (item: object) => void }} props
 */
export default function FormChecklist({ checklist = [], onItemClick }) {
  if (!checklist || checklist.length === 0) {
    return (
      <Box p={4} bg="gray.800" borderRadius="md" borderWidth="1px" borderColor="gray.700">
        <Text color="gray.500" fontSize="sm">No forms required for this shift.</Text>
      </Box>
    );
  }

  const completed = checklist.filter((item) => item.completed);
  const total = checklist.length;
  const requiredTotal = checklist.filter((item) => item.required).length;
  const requiredCompleted = checklist.filter((item) => item.required && item.completed).length;
  const percentage = total > 0 ? Math.round((completed.length / total) * 100) : 0;

  const categoryLabels = {
    preceptor_eval_of_student: "Preceptor Eval",
    student_eval_of_preceptor: "Student Eval",
    student_eval_of_site: "Site Eval",
    patient_notes: "Patient Notes",
    skill_log: "Skill Log",
    clinical_form: "Clinical Form",
    syllabus_signature: "Syllabus",
    other: "Other",
  };

  return (
    <VStack spacing={3} align="stretch">
      <HStack justify="space-between" px={1}>
        <Text fontSize="sm" fontWeight="600" color="gray.300">
          Form Checklist
        </Text>
        <Badge
          colorScheme={percentage === 100 ? "green" : percentage > 50 ? "yellow" : "red"}
          fontSize="xs"
        >
          {completed.length}/{total} ({percentage}%)
        </Badge>
      </HStack>

      <Progress
        value={percentage}
        colorScheme={percentage === 100 ? "green" : percentage > 50 ? "yellow" : "red"}
        size="sm"
        borderRadius="full"
        bg="gray.700"
      />

      <VStack spacing={1} align="stretch">
        {checklist.map((item, idx) => (
          <HStack
            key={idx}
            p={2}
            borderRadius="md"
            bg={item.completed ? "whiteAlpha.50" : "transparent"}
            cursor={!item.completed && onItemClick ? "pointer" : "default"}
            onClick={() => !item.completed && onItemClick?.(item)}
            _hover={!item.completed && onItemClick ? { bg: "whiteAlpha.100" } : {}}
            transition="background 0.2s"
          >
            <Icon
              as={item.completed ? FiCheckCircle : item.required ? FiXCircle : FiCircle}
              color={item.completed ? "green.400" : item.required ? "red.400" : "gray.500"}
              boxSize={4}
              flexShrink={0}
            />
            <Text
              fontSize="sm"
              color={item.completed ? "gray.400" : "gray.200"}
              textDecoration={item.completed ? "line-through" : "none"}
              flex={1}
            >
              {item.formName || "Unknown Form"}
            </Text>
            <Badge
              fontSize="2xs"
              colorScheme={item.completed ? "green" : item.required ? "red" : "gray"}
              variant="subtle"
            >
              {item.completed
                ? "Done"
                : item.required
                ? "Required"
                : "Optional"}
            </Badge>
            {item.formCategory && (
              <Badge fontSize="2xs" colorScheme="blue" variant="outline">
                {categoryLabels[item.formCategory] || item.formCategory}
              </Badge>
            )}
          </HStack>
        ))}
      </VStack>

      {requiredTotal > 0 && requiredCompleted < requiredTotal && (
        <Text fontSize="xs" color="orange.400">
          {requiredTotal - requiredCompleted} required form(s) still needed
        </Text>
      )}
    </VStack>
  );
}
